import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { LogIn, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_LOGO = 'https://bondmaster.com.in/logoBond.png';
const DEFAULT_THEME = {
  primary: '#1B4D3E',
  primary_hover: '#163d32',
  accent: '#D4AF37',
  light_bg: '#E8F5E9',
  card_bg: '#F8FAF9',
  text_secondary: '#64748b',
};
// Reserved top-level paths that must never be treated as partner codes
const RESERVED_CODES = new Set(['login', 'signup', 'tnc', 'partnerlogin', 'demo', 'admin', 'partner', 'subpartner', 'client', 'api', 'docs', 'static']);

const CustomPartnerLoginPage = () => {
  const { partnerCode: rawPartnerCode } = useParams();
  const partnerCode = useMemo(() => (rawPartnerCode || '').toLowerCase(), [rawPartnerCode]);
  const { login } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [branding, setBranding] = useState(null);
  const [colors, setColors] = useState(DEFAULT_THEME);
  const [loadingBranding, setLoadingBranding] = useState(true);
  const [error, setError] = useState(null);

  const isReserved = RESERVED_CODES.has(partnerCode);

  // Fetch branding + colors
  useEffect(() => {
    if (!partnerCode || isReserved) {
      setError('Invalid login URL. Please contact your administrator.');
      setLoadingBranding(false);
      return;
    }
    let isMounted = true;
    (async () => {
      try {
        const [brandRes, colorRes] = await Promise.all([
          axios.get(`${API_URL}/api/partner-branding/${partnerCode}`).catch(() => null),
          axios.get(`${API_URL}/api/partner-colors/${partnerCode}`).catch(() => null),
        ]);
        if (!isMounted) return;
        if (!brandRes || !brandRes.data) {
          setError('Partner portal not found. Please contact your administrator.');
        } else if (!brandRes.data.has_custom_login_page) {
          setError('This partner does not have a custom login page enabled. Please use the main login.');
          setBranding(brandRes.data); // still apply branding for visual error screen
        } else {
          setBranding(brandRes.data);
          setError(null);
        }
        if (colorRes && colorRes.data) {
          setColors({ ...DEFAULT_THEME, ...colorRes.data });
        }
      } catch (err) {
        if (isMounted) setError('Unable to load partner portal. Please try again later.');
      } finally {
        if (isMounted) setLoadingBranding(false);
      }
    })();
    return () => { isMounted = false; };
  }, [partnerCode, isReserved]);

  // App display name (PWA): partner_app_name → fallback "BondMaster"
  const appDisplayName = useMemo(() => {
    if (!branding) return 'BondMaster';
    return branding.partner_app_name || 'BondMaster';
  }, [branding]);

  // Inject favicon + PWA meta tags + dynamic manifest pointing to backend
  useEffect(() => {
    if (!branding || !branding.has_custom_login_page) return undefined;
    const iconUrl = branding.partner_favicon_url || branding.partner_logo_url || FALLBACK_LOGO;
    const themeColor = colors.primary || '#1B4D3E';
    const prevTitle = document.title;
    document.title = `${appDisplayName} | Sign In`;

    const restoreOps = [];

    const overrideMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (el) {
        const prev = el.getAttribute('content');
        el.setAttribute('content', content);
        restoreOps.push({ el, attr: 'content', prev, remove: false });
      } else {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        el.setAttribute('content', content);
        document.head.appendChild(el);
        restoreOps.push({ el, remove: true });
      }
    };

    const overrideLink = (rel, href, extraAttrs = {}) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (el) {
        const prev = el.getAttribute('href');
        el.setAttribute('href', href);
        Object.entries(extraAttrs).forEach(([k, v]) => el.setAttribute(k, v));
        restoreOps.push({ el, attr: 'href', prev, remove: false });
      } else {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        el.setAttribute('href', href);
        Object.entries(extraAttrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
        restoreOps.push({ el, remove: true });
      }
    };

    const addedExtras = [];
    const addExtraLink = (rel, href, extraAttrs = {}) => {
      const el = document.createElement('link');
      el.setAttribute('rel', rel);
      el.setAttribute('href', href);
      Object.entries(extraAttrs).forEach(([k, v]) => el.setAttribute(k, v));
      el.setAttribute('data-partner-login', '1');
      document.head.appendChild(el);
      addedExtras.push(el);
    };

    const manifestUrl = `${API_URL}/api/partner-manifest/${partnerCode}` +
      `?app_name=${encodeURIComponent(appDisplayName)}` +
      `&start_url=${encodeURIComponent(`/${partnerCode}/login`)}`;

    overrideLink('icon', iconUrl, { type: 'image/png' });
    overrideLink('shortcut icon', iconUrl, { type: 'image/png' });
    overrideLink('apple-touch-icon', iconUrl);
    addExtraLink('apple-touch-icon', iconUrl, { sizes: '152x152' });
    addExtraLink('apple-touch-icon', iconUrl, { sizes: '180x180' });
    addExtraLink('apple-touch-icon', iconUrl, { sizes: '167x167' });
    overrideLink('manifest', manifestUrl);

    overrideMeta('apple-mobile-web-app-capable', 'yes');
    overrideMeta('mobile-web-app-capable', 'yes');
    overrideMeta('apple-mobile-web-app-status-bar-style', 'black-translucent');
    overrideMeta('apple-mobile-web-app-title', appDisplayName);
    overrideMeta('application-name', appDisplayName);
    overrideMeta('theme-color', themeColor);
    overrideMeta('msapplication-TileColor', themeColor);
    overrideMeta('msapplication-TileImage', iconUrl);

    return () => {
      document.title = prevTitle;
      restoreOps.forEach(op => {
        if (op.remove) {
          try { op.el.parentNode && op.el.parentNode.removeChild(op.el); } catch (e) { /* noop */ }
        } else if (op.prev != null) {
          op.el.setAttribute(op.attr, op.prev);
        }
      });
      addedExtras.forEach(el => { try { el.parentNode && el.parentNode.removeChild(el); } catch (e) { /* noop */ } });
    };
  }, [branding, colors.primary, appDisplayName, partnerCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }
    setIsLoading(true);
    try {
      const user = await login(username, password, partnerCode);
      toast.success(`Welcome back, ${user.entity_name || user.username}!`);
    } catch (err) {
      toast.error('Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingBranding) {
    return (
      <div className="login-container" data-testid="custom-partner-login-page" style={{ background: `linear-gradient(135deg, ${colors.light_bg} 0%, #ffffff 100%)`, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="w-full max-w-md px-4 text-center">
          <div className="animate-spin h-8 w-8 rounded-full mx-auto border-4 border-t-transparent" style={{ borderColor: `${colors.primary}33`, borderTopColor: colors.primary }}></div>
          <p className="mt-4" style={{ color: colors.text_secondary }}>Loading…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="login-container" data-testid="custom-partner-login-page" style={{ background: `linear-gradient(135deg, ${colors.light_bg} 0%, #ffffff 100%)`, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <div className="w-full max-w-md px-4">
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-8 pb-8 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2" style={{ color: colors.primary }}>Access Error</h2>
              <p className="text-[#64748b] mb-6" data-testid="custom-partner-login-error">{error}</p>
              <Button onClick={() => navigate('/login')} className="text-white" style={{ background: colors.primary }} data-testid="custom-partner-login-go-main">Go to Main Login</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const logoUrl = branding?.partner_logo_url || FALLBACK_LOGO;
  const partnerName = branding?.partner_name || 'Partner';

  return (
    <div
      className="login-container"
      data-testid="custom-partner-login-page"
      style={{ background: `linear-gradient(135deg, ${colors.light_bg} 0%, #ffffff 100%)`, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
    >
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8 animate-fadeIn">
          <img
            src={logoUrl}
            alt={`${partnerName} Logo`}
            className="h-20 mx-auto mb-4 max-w-[220px] object-contain"
            data-testid="custom-partner-login-logo"
          />
          <p className="mt-2" style={{ color: colors.text_secondary }}>
            Your trusted bond investment partner
          </p>
        </div>

        <Card className="border-0 shadow-xl animate-fadeIn" style={{ animationDelay: '0.1s', backgroundColor: colors.card_bg }}>
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-heading text-center" style={{ color: colors.primary }}>Sign In</CardTitle>
            <CardDescription className="text-center" style={{ color: colors.text_secondary }}>
              Enter your credentials to access your portfolio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="font-medium" style={{ color: colors.primary }}>Username</Label>
                <Input id="username" type="text" placeholder="Enter your username" value={username} onChange={(e) => setUsername(e.target.value)} className="h-11 bg-slate-50 border-slate-200" data-testid="custom-partner-login-username-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="font-medium" style={{ color: colors.primary }}>Password</Label>
                <Input id="password" type="password" placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-slate-50 border-slate-200" data-testid="custom-partner-login-password-input" />
              </div>
              <Button
                type="submit"
                className="w-full h-11 text-white font-medium rounded-lg transition-all duration-200"
                style={{ background: colors.primary }}
                onMouseEnter={(e) => { e.currentTarget.style.background = colors.primary_hover || colors.primary; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = colors.primary; }}
                disabled={isLoading}
                data-testid="custom-partner-login-submit-btn"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></span>
                    Signing in…
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="h-4 w-4" />
                    Sign In
                  </span>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs mt-8" style={{ color: colors.text_secondary }}>
          © {new Date().getFullYear()} {partnerName}. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default CustomPartnerLoginPage;
